import type { FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import { RedcatApiHandler } from 'polygon-utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import RegistrationFields from '../components/RegistrationFields';
import StandardButton from '../components/StandardButton';
import { IN_DEVELOPMENT } from '../constants';
import { CURRENT_PASSWORD_FIELD, INPUT_FIELDS } from '../constants/inputfields';
import getProfileFields from '../selectors/getProfileFields';
import getRegisterProfileFields from '../selectors/getRegisterProfileFields';
import { syncMember } from '../thunks/syncMember';
import setLocalValue from '../utils/setLocalValue';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';

export const CHANGE_PASSWORD_ROUTE = '/change-password';

const ChangePasswordScreen = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const profileFields = useAppSelector(getProfileFields);
  const registrationFields = useAppSelector(getRegisterProfileFields);
  const isCurrentPasswordRequired = useAppSelector(
    state => state.config.enableRequiredCurrentPasswordForUpdate,
  );
  const minimumPasswordLength = useAppSelector(state => state.config.minimumPasswordLength);

  const confirmPasswordField = profileFields?.find(
    field => field.id === INPUT_FIELDS.ConfirmPassword,
  );

  const isConfirmPasswordRequired =
    confirmPasswordField?.registerMode !== 'disabled' &&
    confirmPasswordField?.editMode !== 'hidden';

  const confirmPasswordMode = confirmPasswordField?.registerMode;

  const schemas: ResetYourPasswordSchema = {
    CurrentPassword: isCurrentPasswordRequired
      ? Yup.string().required('Required')
      : Yup.string().optional(),
    Password: Yup.string().required('Required').min(minimumPasswordLength, 'Too Short'),
    ConfirmPassword: Yup.string()
      .test('required', 'Required', function (value) {
        if (confirmPasswordMode !== 'required') return true;
        return Boolean(value?.trim());
      })
      .test('too-short', 'Too Short', function (value) {
        if (confirmPasswordMode !== 'required') return true;
        return Boolean(value && value.length >= minimumPasswordLength);
      })
      .test('passwords-match', 'Passwords do not match', function (value) {
        if (confirmPasswordMode !== 'required') return true;
        return value === this.parent.Password;
      }),
  };

  const validationSchema = Yup.object().shape(schemas);

  // Function to get initial values of the form
  const getInitialValues = () => {
    const initialValues: any = { Password: '' };

    if (isCurrentPasswordRequired) {
      initialValues.CurrentPassword = '';
    }
    if (isConfirmPasswordRequired) {
      initialValues.ConfirmPassword = '';
    }
    return initialValues;
  };

  const submit = (values: FormikFields, formikHelpers: FormikHelpers<FormikFields>) => {
    const { setSubmitting, resetForm } = formikHelpers;

    const path = '/api/v1/profile';
    const body = { Password: values.Password } as {
      Password: string;
      ConfirmPassword?: string;
      CurrentPassword?: string;
    };
    // 'ConfirmPassword' should not be included when that field's mode is 'disabled'
    if (isConfirmPasswordRequired) body.ConfirmPassword = values.ConfirmPassword;
    if (confirmPasswordMode === 'optional') body.ConfirmPassword = values.Password;
    if (isCurrentPasswordRequired && values.CurrentPassword)
      body.CurrentPassword = values.CurrentPassword;
    RedcatApiHandler.authorisedFetch({
      path,
      method: 'PUT',
      body,
    })
      .then((response: { token: string | undefined; success: any }) => {
        if (IN_DEVELOPMENT && response.token) {
          setLocalValue('memberAuthToken', response.token);
        }

        dispatch(syncMember());

        if (response.success) {
          enqueueSuccessSnackbar(t('updateSuccess'));
          setSubmitting(false);
          resetForm({ values });
        }
      })
      .catch((err: string | Error) => {
        enqueueErrorSnackbar(err);
        setSubmitting(false);
      });
  };

  return (
    <div style={{ margin: '0 20px' }}>
      <Formik
        validationSchema={validationSchema}
        initialValues={getInitialValues()}
        onSubmit={submit}
        enableReinitialize={true}
        validateOnMount
      >
        {({ handleSubmit, touched, isSubmitting, isValid }) => {
          return (
            <Form onSubmit={handleSubmit} style={styles.registrationForm}>
              <>
                {isCurrentPasswordRequired && (
                  <RegistrationFields
                    field={{ ...CURRENT_PASSWORD_FIELD, label: t('currentPassword') }}
                  />
                )}
                {registrationFields &&
                  registrationFields.visible
                    .filter((field: FormikFields) => field.id === INPUT_FIELDS.Password)
                    .map((field: RegisterFields, index: any) => (
                      <RegistrationFields
                        key={index}
                        field={{ ...field, label: t('newPassword'), mode: '' }}
                        showStrengthBar={isConfirmPasswordRequired ? false : true}
                      />
                    ))}

                {isConfirmPasswordRequired &&
                  profileFields &&
                  profileFields
                    .filter((field: FormikFields) => field.id === INPUT_FIELDS.ConfirmPassword)
                    .map((field: RegisterFields, index: any) => (
                      <RegistrationFields
                        key={index}
                        field={{ ...field, label: t('confirmPassword'), mode: '' }}
                        showStrengthBar={true}
                      />
                    ))}

                <div style={styles.buttonContainer}>
                  <StandardButton
                    label={t('button.updatePassword')}
                    containerStyle={styles.button}
                    disabled={Boolean(isSubmitting || !isValid || !Object.keys(touched).length)}
                    themeKey="signInSubmitButton"
                    onClick={handleSubmit}
                  />
                </div>
              </>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const styles: Styles = {
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    borderRadius: 5,
    height: 40,
    width: '100%',
    padding: 0,
  },

  column: {
    minWidth: 250,
  },
  columnMobile: {
    padding: '20px 0 0 0',
  },

  verticalSeperator: {
    width: 1.5,
    marginLeft: 40,
    marginRight: 40,
  },
  registrationForm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'strech',
    justifyContent: 'space-between',
    paddingTop: 40,
  },
};

export default ChangePasswordScreen;
